import { useQuery } from '@tanstack/react-query';
import type { BlockResourceAvailabilityRecord } from '@/types/block-availability-api';
import { getBlockAppointmentFrom } from './use-block-appointment';

/**
 * `GET /api/Appointment/BlockResourceAppointment?id={id}` — the DEVICE
 * block-hydration route (PROD-2512, Scheduling `5acfb488`).
 *
 * The twin of `useBlockAppointment`, and a SEPARATE hook rather than a flag on
 * it — exactly as `useSaveBlockResourceSlot` sits beside `useSaveBlockSlot` and
 * `useUnblockResourceSlot` beside `useUnblockSlot`, and exactly how the backend
 * mirrored it (Andrew, 2026-09-22: "lets mirror better because if later they
 * became different").
 *
 * The request is IDENTICAL, and that is not an oversight:
 * `BlockResourceAppointmentQuery` DERIVES from `BlockAppointmentQuery` and adds
 * no wire field, because a block is addressed by its slot id and nothing else.
 * Underneath, one service method serves both. The route is the only thing the
 * two hooks disagree about, so the GET itself is imported from the provider
 * file rather than restated here — that is the client's
 * `GetBlockAppointmentCore`.
 *
 * WHY THE SPLIT EXISTS, since an identical request makes it look redundant.
 *
 * `GetBlockAppointmentAsync2` resolved the block's actor id through the
 * practitioner registry unconditionally. A device id matches no practitioner,
 * PracticeManagement returned null from a unary gRPC handler, null could not be
 * serialised, and reopening a device block answered a bare 500 — an error toast
 * over an empty form, the last broken step in the device block lifecycle, since
 * rendering the tile (PROD-2382) and unblocking it (PROD-2510) already worked.
 * Both routes now refuse the other kind outright:
 *
 *   provider route + device block      → 400 "This block belongs to a device;
 *                                             open it through the device route."
 *   device route + practitioner block  → 400 "This block belongs to a
 *                                             practitioner; open it through the
 *                                             provider route."
 *
 * So this is not a nicety and the choice is not cosmetic: hydrating a device
 * block through `useBlockAppointment` is now a guaranteed 400. That is why
 * `BlockAvailabilityPanel` picks the hook from `state.actorKind` — the
 * discriminant that already decides the picker, the copy, the shift feed and
 * both mutation routes — and from nothing else.
 *
 * WHAT COMES BACK THAT DID NOT BEFORE. The device is named as a `"Device"`
 * participant rather than through the practitioner slot, shaped exactly as
 * PROD-2382 shapes a blocked device slot on `ResourceList` and PROD-2510 on the
 * SignalR broadcast — so the tile the operator clicked and the record the panel
 * hydrates from are the same object. `practitioner` is null on this route.
 *
 * Its own query key, for the same reason it is its own hook: a shared key would
 * put two different endpoints' responses in one cache bucket, and a device
 * block and a practitioner block can hold the same slot id in neither
 * direction — but the SHAPES differ, and one bucket would let a stale provider
 * record hydrate a device panel.
 */
export function useBlockResourceAppointment(blockId: string | null) {
  return useQuery({
    queryKey: ['block-resource-appointment', blockId],
    queryFn: ({ signal }) => {
      if (!blockId) throw new Error('useBlockResourceAppointment: blockId is required');
      return getBlockAppointmentFrom<BlockResourceAvailabilityRecord>(
        '/api/Appointment/BlockResourceAppointment',
        blockId,
        signal,
      );
    },
    enabled: !!blockId,
  });
}
